import styled from '@emotion/styled'
import React, { useEffect, useRef } from 'react'

import { SWIPE_THRESHOLD } from '../../constants'
import { type TabMenuOption, TabsMenuButton } from './TabsMenuButton'

type Props = {
  activeMenu: TabMenuOption
  textMain: string
  textSecondary: string
  onChange: (menu: TabMenuOption) => void
  enableSwipe?: boolean
}

const TabsMenuContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

export const TabsMenu: React.FC<Props> = ({
  activeMenu,
  textMain,
  textSecondary,
  onChange,
  enableSwipe = true,
}) => {
  const touchStartX = useRef<number | null>(null)

  useEffect(() => {
    if (!enableSwipe) return
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null) return

      const deltaX = e.changedTouches[0].clientX - touchStartX.current
      touchStartX.current = null

      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return

      if (deltaX < 0 && activeMenu !== 'secondary') {
        onChange('secondary')
      } else if (deltaX > 0 && activeMenu !== 'main') {
        onChange('main')
      }
    }

    document.body.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.body.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      document.body.removeEventListener('touchstart', handleTouchStart)
      document.body.removeEventListener('touchend', handleTouchEnd)
    }
  }, [activeMenu, onChange, enableSwipe])

  const buttons = [
    {
      variant: 'main',
      text: textMain,
    },
    {
      variant: 'secondary',
      text: textSecondary,
    },
  ] as const

  return (
    <TabsMenuContainer>
      {buttons.map((button) => (
        <TabsMenuButton
          key={button.variant}
          variant={button.variant}
          active={activeMenu === button.variant}
          onChange={onChange}
          buttonText={button.text}
        />
      ))}
    </TabsMenuContainer>
  )
}
